import { HEADER_CLICK_IDS } from '../../types';
import { getLocaleFormattedNumber } from '@dx-ui/framework-i18n';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import FocusLock from 'react-focus-lock';
import Icon from '@dx-ui/osc-icon';

import { dispatchGlobalNavigationClick } from '../../header.utilities';
import { MobileAccountUserLink } from './account-user-link';
import { useHeaderContext } from '../../context/header-context';
import { useHeaderStateContext } from '../../context/header-state-context';

export const MobileAccountSidebarItems = () => {
  const {
    user: { user, onSignOut },
    features: { shouldSuppressHonorsAccountLinks, shouldOpenAccountLinksInNewWindow },
    navigation: { onGlobalNavigationClick, isLegacyGlobalNavigationClickAnalyticsEnabled = true },
  } = useHeaderContext();

  const {
    menuState: { setIsAccountOpen },
    activeUserLinks: userLinks,
  } = useHeaderStateContext();
  const { locale = 'en' } = useRouter();
  const { t } = useTranslation('osc-header');

  const onClose = () => setIsAccountOpen(false);

  if (!user) {
    return null;
  }
  const formattedHonorsPointsTotal = getLocaleFormattedNumber(user.honorsPoints, { locale });

  return (
    <FocusLock returnFocus>
      <div className="relative mb-2 border-b border-surface px-4 py-3 ">
        <button onClick={onClose} type="button" className="px-2">
          <Icon name="close" size="md" className="m-auto" />
          <span className="text--xs font-semibold brand-ey:font-normal brand-wa:font-normal">
            {t('close')}
            <span className="sr-only">{t('closeAccountDetails')}</span>
          </span>
        </button>
      </div>
      <div className="flex">
        <div className="px-4">
          <div className="flex size-12 items-center justify-center rounded-full bg-points p-2">
            <Icon name="user" size="xl" />
          </div>
        </div>
        <div>
          <div className="osc-nav-account-text">{t('greetings', { username: user.name })}</div>
          <div className="osc-nav-account-text capitalize">
            <span className="underline">{user.honorsTier}</span> {t('status')}
          </div>
          <div className="osc-nav-account-text">
            {t('honorsPointsTotal', { honorsPointsTotal: formattedHonorsPointsTotal })}
          </div>
          {user.hhonorsNumber ? (
            <div className="osc-nav-account-text">
              {t('hiltonHonorsNumber', { hhonorsNumber: user.hhonorsNumber })}
            </div>
          ) : null}
        </div>
      </div>
      <div className="mt-4 divide-y divide-solid border-y border-surface">
        {userLinks?.accountLink?.url ? (
          <MobileAccountUserLink
            url={userLinks.accountLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                HEADER_CLICK_IDS.MemberNameHiltonHonorsHome,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('hiltonHonorsHome')}
          </MobileAccountUserLink>
        ) : null}
        {userLinks?.honorsProfileLink?.url ? (
          <MobileAccountUserLink
            url={userLinks.honorsProfileLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                isLegacyGlobalNavigationClickAnalyticsEnabled
                  ? HEADER_CLICK_IDS.MemberNameHiltonHonorsProfile
                  : HEADER_CLICK_IDS.MemberNameProfile,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('profile')}
          </MobileAccountUserLink>
        ) : null}

        {userLinks?.honorsActivityLink?.url && !shouldSuppressHonorsAccountLinks ? (
          <MobileAccountUserLink
            url={userLinks.honorsActivityLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                isLegacyGlobalNavigationClickAnalyticsEnabled
                  ? HEADER_CLICK_IDS.MemberNameHiltonHonorsActivity
                  : HEADER_CLICK_IDS.MemberNameActivity,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('activity')}
          </MobileAccountUserLink>
        ) : null}

        {userLinks?.yourOffersLink?.url && !shouldSuppressHonorsAccountLinks ? (
          <MobileAccountUserLink
            url={userLinks.yourOffersLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                HEADER_CLICK_IDS.MemberNameHiltonHonorsYourOffers,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('yourOffers')}
          </MobileAccountUserLink>
        ) : null}

        {userLinks?.honorsPointsLink?.url && !shouldSuppressHonorsAccountLinks ? (
          <MobileAccountUserLink
            url={userLinks.honorsPointsLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                isLegacyGlobalNavigationClickAnalyticsEnabled
                  ? HEADER_CLICK_IDS.MemberNameHiltonHonorsPoints
                  : HEADER_CLICK_IDS.MemberNamePoints,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('pointsLabel')}
          </MobileAccountUserLink>
        ) : null}

        {userLinks?.goHiltonLink?.url ? (
          <MobileAccountUserLink
            url={userLinks.goHiltonLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                HEADER_CLICK_IDS.MemberNameGoHiltonProgramDetails,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('goHiltonProgramDetails')}
          </MobileAccountUserLink>
        ) : null}

        {userLinks?.friendsAndFamilyLink?.url ? (
          <MobileAccountUserLink
            url={userLinks.friendsAndFamilyLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                HEADER_CLICK_IDS.MemberNameManageFriendsAndFamily,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('manageFriendsAndFamily')}
          </MobileAccountUserLink>
        ) : null}

        {userLinks?.hgvMaxLink?.url ? (
          <MobileAccountUserLink
            url={userLinks.hgvMaxLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                HEADER_CLICK_IDS.MemberNameHgvMaxRateDetails,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('hgvMaxRateDetails')}
          </MobileAccountUserLink>
        ) : null}

        {userLinks?.b2bMemberLink?.url ? (
          <MobileAccountUserLink
            url={userLinks.b2bMemberLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                HEADER_CLICK_IDS.MemberNameManageHiltonForBusiness,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {userLinks?.b2bMemberLink?.label}
          </MobileAccountUserLink>
        ) : null}

        <button
          className="focus:osc-nav-drawer-link-active hover:osc-nav-drawer-link-active osc-nav-account-text block w-full border-surface px-6 py-2 text-start text-surface"
          onClick={async () => {
            dispatchGlobalNavigationClick(
              HEADER_CLICK_IDS.MemberNameSignOut,
              onGlobalNavigationClick,
              isLegacyGlobalNavigationClickAnalyticsEnabled
            );
            onClose();
            await onSignOut();
          }}
          type="button"
        >
          {t('signOut')}
        </button>
      </div>
    </FocusLock>
  );
};
